import { Vector2 } from 'three'
import { MAP_MAX_SIZE } from '../../../core/state/StateMap.type'
import getGridIndexListItemByXYOrNull from '../../../core/tile_position_xy/GridIndex/getGridIndexListItemOrNull'
import getFirstSetValue from '../../../ldsh/getFirstSetValue'

type Edge = {
  // start: string
  // end: string
  start: number
  end: number
  startPt: Vector2
  endPt: Vector2
};

type LoopPoint = {
  v2 : Vector2
  edge : Edge
}

const tmpFrom = new Vector2()
const tmpTo = new Vector2()

type BorderDirection = {
  dx: number
  dy: number
  from: (x: number, y: number) => Vector2
  to:   (x: number, y: number) => Vector2
}

const dirs: Array<BorderDirection> = [
  { dx: 0, dy: 1, // top
    from: (x: number, y: number) => (tmpFrom.set(x, y + 1)),
    to:   (x: number, y: number) => (tmpTo.set(x + 1, y + 1))
  },
  { dx: 1, dy: 0, // right
    from: (x: number, y: number) => (tmpFrom.set(x + 1, y + 1)),
    to:   (x: number, y: number) => (tmpTo.set(x + 1, y))
  },
  { dx: 0, dy: -1, // bottom
    from: (x: number, y: number) => (tmpFrom.set(x + 1, y)),
    to:   (x: number, y: number) => (tmpTo.set(x, y))
  },
  { dx: -1, dy: 0, // left
    from: (x: number, y: number) => (tmpFrom.set(x, y)),
    to:   (x: number, y: number) => (tmpTo.set(x, y + 1))
  }
]

const key = (v2: Vector2) => v2.x + (v2.y * MAP_MAX_SIZE)

/**
 * Generates one or more closed-loop points around groups of "inside" tiles in a 2D grid.
 * Uses a simple boundary-tracing algorithm: for each inside cell, collect its external edges,
 * link edges into loops, then create a SplineCurve for each loop.
 */
export default function createBorderLoopsCollectionV2(
  originX: number,
  originY: number,
  gridWidth: number,
  gridHeight: number,
  maxManhattanDistance: number,
  insideBorder: (x: number, y: number) => boolean
): Array<Array<LoopPoint>> {
  console.log('createBorderLoopsCollectionV2')
  // console.profile('createBorderLoopsCollectionV2')
  // Build binary mask of inside cells within the grid and max distance
  const inside: Array<boolean> = new Array(gridWidth * gridHeight)

  for (let y = 0; y < gridHeight; y++) {
    // inside[y] = []
    const absY = Math.abs(y - originY)
    for (let x = 0; x < gridWidth; x++) {
      const insideIndex = x + (y * gridWidth)
      const manh = Math.abs(x - originX) + absY
      const insideManhattanDistance = manh <= maxManhattanDistance
      if (insideManhattanDistance) {
        inside[insideIndex] = insideBorder(x, y)
      } else {
        inside[insideIndex] = false
      }
    }
  }

  // Helper to turn a point into a unique string key
  // const key = (x: number, y: number) => `${x},${y}`
  // const key = (v2: Vector2) => `${v2.x},${v2.y}`

  // Collect all boundary edges: for each inside cell, any side with neighbor false
  const edges: Edge[] = [];

  console.time('createBorderLoopsCollectionV2.collectEdges')
  let insideIndex = 0
  for (let y = 0; y < gridHeight; y++) {
    for (let x = 0; x < gridWidth; x++) {
      insideIndex = x + (y * gridWidth)

      if (inside[insideIndex]) {
        for (const { dx, dy, from, to } of dirs) {
          const nx = x + dx
          const ny = y + dy
          const neighborInside = getGridIndexListItemByXYOrNull(gridWidth, gridHeight, inside, nx, ny)
          if (!neighborInside) {
            const fromVector = from(x, y)
            const toVector = to(x, y)
            const startPt = fromVector.clone()
            const endPt = toVector.clone()
            edges.push({
              start: key(fromVector),
              end: key(toVector),
              startPt,
              endPt,
            })
          }
        }
      }
    }
  }
  console.timeEnd('createBorderLoopsCollectionV2.collectEdges')

  // Link edges into closed loops
  const loops: LoopPoint[][] = []
  const unused = new Set(edges.map((e, i) => i))

  console.time('createBorderLoopsCollectionV2.linkEdgesIntoClosedLoops')
  while (unused.size > 0) {
    // const startIdx = unused.values().next().value!
    const startIdx = getFirstSetValue(unused)!
    unused.delete(startIdx)
    const firstEdge = edges[startIdx]
    const loop: LoopPoint[] = [
      { v2: firstEdge.startPt.clone(), edge: firstEdge },
      { v2: firstEdge.endPt.clone(), edge: firstEdge },
    ]
    let lastKey = edges[startIdx].end

    let found = true
    while (found) {
      found = false
      for (const i of unused) {
        const e = edges[i]
        if (e.start === lastKey) {
          // append edge
          loop.push({ v2: e.endPt.clone(), edge: e })
          lastKey = e.end
          unused.delete(i)
          found = true
          break
        }
      }
    }

    // If loop doesn't close exactly, last point should equal first; otherwise skip
    const firstV2 = loop[0].v2
    const firstKey = key(firstV2)
    if (lastKey === firstKey) {
      loops.push(loop)
    }
  }
  console.timeEnd('createBorderLoopsCollectionV2.linkEdgesIntoClosedLoops')

  console.profileEnd('createBorderLoopsCollectionV2')
  // console.log('loopsV2', loops)
  return loops
}
